/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { EMPTY, from, of } from 'rxjs';
import { concatMap, switchMap, take, tap, map } from 'rxjs/operators';
import { TicketFields } from '@touchplan/common';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { ShallowRequest } from './shallow-request.service';

import firebase from 'firebase/compat/app';

export interface IProjectTicketListMetadata {
	list: TicketFields[];
	currentIdx: number;
	length: number;
	projectId: string;
	planId: string;
}

interface IPartialProjectTicketListMetadata extends Omit<IProjectTicketListMetadata, 'list, currentIdx'> {}

@Injectable({
	providedIn: 'root',
})
export class ProjectTicketsService {
	constructor(private db: AngularFireDatabase, private shallowRequest: ShallowRequest) {}

	/**
	 * Accepts a projectId and returns an observable stream emitting a ticketList metadata object [[IProjectTicketListMetadata]] on each emission.
	 * Each ticket list request will fire sequentially, allowing the caller to only have a plan's worth of ticket data loaded at one time.
	 * This is _not_ a continuous subscription and will fire only once per plan, then completed.
	 * Errors can be thrown if you do not have access to the project you request.
	 * Will emit nothing and just complete if the project has no tickets
	 *
	 * ```
	 * projectTickets.requestProjectTickets('-LOt4AWX9LB8hJdwA87j').subscribe({ next: (data) => {
	 * 	logger.log('request get', data);
	 * 	size += data.list.length;
	 * }, error: (err) => {
	 * 	logger.log('err get', err);
	 * }, complete: () => {
	 * 	logger.log('done', size);
	 * }});
	 * ```
	 */
	public requestProjectTickets(projectId: string) {
		return this.shallowRequest.request(this.getTicketRef(projectId)).pipe(
			//spread out an emission of an array to multiple emissions
			switchMap((pIdList) => {
				if (!pIdList) {
					return EMPTY;
				}
				return from(pIdList).pipe(
					map(
						(planId) =>
							({
								length: pIdList.length,
								projectId,
								planId,
							} as IPartialProjectTicketListMetadata)
					)
				);
			}),
			//request each sublist one at a time (next does not run until the previous completes)
			concatMap((partialMetadata, index) => {
				if (!partialMetadata) {
					return of(null as IProjectTicketListMetadata);
				}
				const planId = partialMetadata.planId;

				const ref = this.getTicketRef(projectId, planId);
				return this.db
					.list(ref)
					.valueChanges()
					.pipe(
						take(1),
						map((tickets) => {
							return Object.assign(partialMetadata, {
								list: tickets,
								currentIdx: index,
							}) as IProjectTicketListMetadata;
						})
					);
			})
		);
	}

	private getTicketRef(projectId: string, planId?: string) {
		const path = fancyFirebaseGetters.getTicketPath(projectId, planId);
		return fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(path, projectId);
	}
}
